import React, { useState, useMemo } from 'react';
import { Link } from 'react-router';
import { PlanningPageType } from './PlanningPage';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useIncident } from '../hooks/incidentHooks';
import { PlanningAgenda } from './PlanningAgenda';
import { PLANNING_PHASE, widgetsHiddenForFamily } from './USCGPage';
import { useIcsFormFamily } from '../hooks/useIcsFormFamily';
import { useICSContext } from './ICSContext';
import { AdvanceOperationalPeriodModal } from '../AdvanceOperationalPeriodModal';
import { PREMIUM_RESOURCE_WIDGETS } from '../premiumResourceManagement';
import {
  usePositionWorkspacesFlag,
  usePremiumResourceManagementFlag,
} from 'utils/featureFlags';
import {
  buildFormsDropdownOptions,
  useWidgetRenderContext,
} from './widgetRegistry';
import { FormPaneRow } from './FormPaneRow';
import { seatSlugForPlanningPhase } from '../positionWorkspace/seatCatalog';
import { cn } from '@/lib/utils';

type PlanningSectionProps = {
  planningPhases: PlanningPageType[];
  currentPhase: number;
  updatePhase: (phase: number) => void;
  showAgenda: boolean;
};

const REMOVED_WIDGETS = ['agenda', 'initialResponse'];

const PlanningSection: React.FC<PlanningSectionProps> = ({
  planningPhases,
  currentPhase,
  updatePhase,
  showAgenda,
}) => {
  const [
    showAdvanceOperationalPeriodModal,
    setShowAdvanceOperationalPeriodModal,
  ] = useState(false);
  const currentPhaseData: PlanningPageType = planningPhases[currentPhase];
  const { card, setCard, isFuture, effectivePeriodNumber } = useICSContext();
  const premiumResourceManagement = usePremiumResourceManagementFlag();
  const positionWorkspaces = usePositionWorkspacesFlag();
  const formFamily = useIcsFormFamily();
  const excludedWidgets = useMemo(
    () => [
      ...(premiumResourceManagement
        ? REMOVED_WIDGETS
        : [...REMOVED_WIDGETS, ...PREMIUM_RESOURCE_WIDGETS]),
      ...widgetsHiddenForFamily(formFamily),
    ],
    [formFamily, premiumResourceManagement]
  );

  const widgetOrder = useMemo(() => {
    if (!currentPhaseData) return [];
    return (
      currentPhaseData.widgetOrder?.filter(
        (widgetName) => !excludedWidgets.includes(widgetName)
      ) ||
      Object.keys(currentPhaseData.widgets).filter(
        (widgetName) => !excludedWidgets.includes(widgetName)
      ) ||
      []
    );
  }, [currentPhaseData, excludedWidgets]);

  const incident = useIncident();
  const positionWorkspaceSlug = currentPhaseData
    ? seatSlugForPlanningPhase(currentPhaseData.title)
    : undefined;
  const widgetContext = useWidgetRenderContext(planningPhases, currentPhase);

  // All selectable forms: current phase order first, then remaining WIDGET_TITLES
  const formsDropdownOptions = useMemo(
    () =>
      buildFormsDropdownOptions({
        widgetOrder,
        exclude: excludedWidgets,
      }),
    [widgetOrder, excludedWidgets]
  );

  //Always makes sure a card is rendered
  const widgetToRender = useMemo(() => {
    if (!formsDropdownOptions.length) return '';

    if (card && formsDropdownOptions.some((option) => option.value === card)) {
      return card;
    }

    return formsDropdownOptions[0]?.value ?? '';
  }, [formsDropdownOptions, card]);

  return (
    <>
      <div className="planning-section">
        <div className="d-flex align-items-center mb-2 flex-row flex-wrap gap-2">
          {isFuture ? (
            <span
              data-tour="working-op-indicator"
              className="tw:text-xs tw:font-medium tw:text-muted-foreground"
            >
              Working OP: {effectivePeriodNumber}
            </span>
          ) : null}
          {incident.data?.id ? (
            <Link
              to={`/iap/${incident.data.id}/approved-forms`}
              data-tour="approved-ics-forms"
              className="tw:text-xs tw:font-medium tw:text-primary tw:underline"
            >
              Approved ICS Forms
            </Link>
          ) : null}
          {positionWorkspaces && positionWorkspaceSlug && incident.data?.id ? (
            <Link
              to={`/iap/${incident.data.id}/seat/${positionWorkspaceSlug}?returnPhase=${currentPhase + 1}`}
              data-tour="open-position-workspace"
            >
              Open position workspace
            </Link>
          ) : null}
          {currentPhaseData.title === PLANNING_PHASE.OPERATIONS_BRIEFING && (
            <StylishNewButton
              className="button--secondary"
              data-tour="planning-advance-op"
              onClick={() => setShowAdvanceOperationalPeriodModal(true)}
            >
              Advance Operational Period
            </StylishNewButton>
          )}
        </div>
        <div className="tw:flex tw:min-w-0 tw:gap-2">
          {showAgenda && (
            <div
              className="tw:min-w-0 tw:flex-1 tw:basis-1/2"
              data-tour="planning-agenda"
            >
              <PlanningAgenda
                planningPhases={planningPhases}
                currentPhase={currentPhase}
              />
            </div>
          )}
          <div
            className={cn(
              'tw:min-w-0',
              showAgenda ? 'tw:flex-1 tw:basis-1/2' : 'tw:w-full'
            )}
            data-tour="planning-forms"
          >
            {incident.data?.id ? (
              <FormPaneRow
                key={currentPhase}
                options={formsDropdownOptions}
                initialWidget={widgetToRender}
                ctx={widgetContext}
                incidentId={String(incident.data.id)}
                onPrimaryChange={setCard}
              />
            ) : null}
          </div>
        </div>

        <div
          className={
            currentPhase > 0
              ? 'd-flex justify-content-between mt-3'
              : 'd-flex justify-content-end mt-3'
          }
        >
          {currentPhase > 0 && (
            <StylishNewButton
              className="button button--secondary"
              onClick={() => updatePhase(currentPhase - 1)}
            >
              <SharedIcon iconName="arrow_back" classes="me-2" />
              Previous Phase
            </StylishNewButton>
          )}
        </div>
      </div>
      {showAdvanceOperationalPeriodModal && (
        <AdvanceOperationalPeriodModal
          showModal={showAdvanceOperationalPeriodModal}
          setShowModal={setShowAdvanceOperationalPeriodModal}
          data={incident.data}
        />
      )}
    </>
  );
};

export default PlanningSection;
